import { Theme } from '@mui/material';
import { alpha } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';

import { ALPHA_VOICE_COLOURS } from 'components/Alpha/alphaVoiceColours';

const mutedText = (theme: Theme): string => alpha(theme.palette.text.primary, 0.85);
const controlBorder = (theme: Theme): string => alpha(theme.palette.text.primary, 0.6);
const hairline = (theme: Theme): string => alpha(theme.palette.text.primary, 0.14);

const focusRing = (theme: Theme, offset: number): Record<string, unknown> => ({
  outline: `2px solid ${theme.palette.text.primary}`,
  outlineOffset: offset,
});

const useStyles = makeStyles((theme: Theme) => ({
  '@keyframes alphaPulse': {
    '0%, 80%, 100%': { opacity: 0.25 },
    '40%': { opacity: 1 },
  },
  root: {
    isolation: 'isolate',
    position: 'relative',
    display: 'flex',
    flexDirection: 'column',
    width: '100%',
    height: '100%',
    minWidth: 0,
    minHeight: 0,
    boxSizing: 'border-box',
    background:
      theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.surface.card,
    color: theme.palette.text.primary,
    padding: theme.spacing(1.25),
    gap: theme.spacing(1),
    [theme.breakpoints.down(400)]: {
      padding: theme.spacing(0.75),
      gap: theme.spacing(0.75),
    },
  },
  rootCompact: {
    '& $emptyState, & $emptyState span': {
      fontSize: theme.podScale.font.md,
    },
    '& $errorTitle': {
      fontSize: theme.podScale.font.lg,
    },
    '& $newChatButton': {
      borderRadius: theme.podScale.radius.control,
      fontSize: theme.podScale.font.md,
    },
    '& $starterPromptButton': {
      fontSize: theme.podScale.font.sm,
    },
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    gap: theme.spacing(1),
    flexShrink: 0,
    paddingBottom: theme.spacing(0.75),
    borderBottom: `1px solid ${hairline(theme)}`,
    minWidth: 0,
  },
  headerContext: {
    display: 'flex',
    flexDirection: 'column',
    gap: '2px',
    minWidth: 0,
  },
  headerTitle: {
    ...theme.typography.subtitle2,
    fontWeight: 700,
    margin: 0,
    minWidth: 0,
  },
  headerSubtitle: {
    ...theme.typography.caption,
    color: mutedText(theme),
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    whiteSpace: 'nowrap',
    minWidth: 0,
  },
  headerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    flexShrink: 0,
  },
  iconButton: {
    flexShrink: 0,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: 8,
    border: `1px solid ${controlBorder(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  body: {
    position: 'relative',
    flex: '1 1 0',
    minHeight: 0,
    display: 'flex',
  },
  transcript: {
    flex: '1 1 0',
    minWidth: 0,
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1.25),
    paddingRight: theme.spacing(0.5),
    '&::-webkit-scrollbar': { width: '6px' },
    '&::-webkit-scrollbar-track': { background: 'transparent' },
    '&::-webkit-scrollbar-thumb': {
      background: alpha(theme.palette.text.primary, 0.32),
      borderRadius: '3px',
    },
  },
  historyOverlay: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    background: theme.palette.background.default,
    boxShadow: theme.palette.mode === 'dark' ? 'none' : '-2px 0 8px rgba(0, 20, 45, 0.14)',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    padding: theme.spacing(1),
    boxSizing: 'border-box',
  },
  historyHeader: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexShrink: 0,
  },
  historyList: {
    flex: '1 1 0',
    minHeight: 0,
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  historyEmpty: {
    flex: '1 1 0',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    textAlign: 'center',
    padding: theme.spacing(2),
    ...theme.typography.body2,
    color: mutedText(theme),
  },
  historyItem: {
    boxSizing: 'border-box',
    width: '100%',
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: 2,
    textAlign: 'left',
    padding: theme.spacing(0.75, 1),
    borderRadius: 8,
    border: `1px solid ${hairline(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  historyItemTitle: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
  },
  historyItemSubtitle: {
    display: 'block',
    width: '100%',
    minWidth: 0,
    whiteSpace: 'nowrap',
    overflow: 'hidden',
    textOverflow: 'ellipsis',
    ...theme.typography.caption,
    color: mutedText(theme),
  },
  newChatButton: {
    flexShrink: 0,
    boxSizing: 'border-box',
    padding: theme.spacing(0.75, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.text.primary}`,
    background: alpha(theme.palette.text.primary, 0.1),
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.text.primary, 0.18) },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  emptyState: {
    flex: '1 1 0',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(0.75),
    textAlign: 'center',
    padding: theme.spacing(2),
    ...theme.typography.body2,
    color: mutedText(theme),
  },
  errorTitle: {
    ...theme.typography.subtitle2,
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  wolfMark: {
    width: 40,
    height: 40,
    borderRadius: '50%',
    background: theme.palette.primary.main,
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    '& img': { width: 24, height: 24 },
  },
  starterPrompts: {
    display: 'flex',
    flexWrap: 'wrap',
    gap: theme.spacing(0.5),
    justifyContent: 'center',
  },
  starterPromptButton: {
    boxSizing: 'border-box',
    padding: theme.spacing(0.5, 1),
    borderRadius: 999,
    border: `1px solid ${controlBorder(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.caption,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:disabled': { opacity: 0.5, cursor: 'not-allowed' },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  row: {
    display: 'flex',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  rowMine: {
    flexDirection: 'row-reverse',
  },
  cardsRow: {
    marginTop: theme.spacing(-0.75),
  },
  avatarSpacer: {
    visibility: 'hidden',
  },
  avatar: {
    flexShrink: 0,
    width: 24,
    height: 24,
    borderRadius: '50%',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    ...theme.typography.caption,
    fontWeight: 700,
  },
  avatarAlpha: {
    background: theme.palette.primary.main,
    '& img': { width: 16, height: 16 },
  },
  avatarUser: {
    background: theme.palette.primary.main,
    color: '#fff',
  },
  content: {
    minWidth: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    maxWidth: '85%',
  },
  contentWide: {
    flex: '1 1 0',
    maxWidth: '100%',
  },
  toolCallLine: {
    ...theme.typography.caption,
    color: mutedText(theme),
    fontStyle: 'italic',
  },
  bubble: {
    boxSizing: 'border-box',
    borderRadius: 10,
    padding: theme.spacing(0.75, 1.25),
    ...theme.typography.body2,
    wordBreak: 'break-word',
    border: `1px solid ${hairline(theme)}`,
    background: theme.palette.secondary.main,
  },
  markdown: {
    '& > *:first-child': { marginTop: 0 },
    '& > *:last-child': { marginBottom: 0 },
    '& p': { margin: theme.spacing(0.75, 0) },
    '& ul, & ol': { margin: theme.spacing(0.5, 0), paddingLeft: 16 },
    '& li': { margin: theme.spacing(0.25, 0) },
    '& li > ul, & li > ol': { margin: theme.spacing(0.25, 0) },
    '& h1, & h2, & h3, & h4, & h5, & h6': {
      ...theme.typography.body2,
      fontWeight: 700,
      margin: theme.spacing(0.75, 0, 0.25),
    },
    '& strong': { fontWeight: 700 },
    '& code': {
      fontFamily: 'monospace',
      fontSize: '0.9em',
      background: alpha(theme.palette.text.primary, 0.08),
      borderRadius: 4,
      padding: '1px 4px',
    },
    '& pre': {
      fontFamily: 'monospace',
      fontSize: '0.85em',
      background: alpha(theme.palette.text.primary, 0.08),
      borderRadius: 6,
      padding: theme.spacing(0.75),
      overflowX: 'auto',
      '& code': { background: 'none', padding: 0 },
    },
    '& a': { color: 'inherit', textDecoration: 'underline' },
  },
  markdownTableWrapper: {
    maxWidth: '100%',
    overflowX: 'auto',
    '& table': {
      borderCollapse: 'collapse',
      ...theme.typography.caption,
    },
    '& th, & td': {
      border: `1px solid ${hairline(theme)}`,
      padding: theme.spacing(0.375, 0.75),
      textAlign: 'left',
      verticalAlign: 'top',
      wordBreak: 'normal',
      overflowWrap: 'break-word',
    },
    '& th': {
      whiteSpace: 'nowrap',
    },
    '& td': {
      minWidth: '12ch',
    },
  },
  bubbleMine: {
    background: theme.palette.primary.main,
    color: '#fff',
    border: 'none',
    whiteSpace: 'pre-wrap',
  },
  typingDots: {
    display: 'inline-flex',
    gap: '4px',
    padding: theme.spacing(0.75, 1.25),
  },
  dot: {
    width: 6,
    height: 6,
    borderRadius: '50%',
    background: theme.palette.text.primary,
    animation: '$alphaPulse 1200ms ease-in-out infinite',
    '&:nth-child(2)': { animationDelay: '160ms' },
    '&:nth-child(3)': { animationDelay: '320ms' },
    '@media (prefers-reduced-motion: reduce)': { animation: 'none', opacity: 0.6 },
  },
  confirmCard: {
    boxSizing: 'border-box',
    border: `1px solid ${theme.palette.text.primary}`,
    borderRadius: 10,
    padding: theme.spacing(1),
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    minWidth: 0,
  },
  confirmLabel: {
    ...theme.typography.caption,
    fontWeight: 700,
    letterSpacing: '0.04em',
    textTransform: 'uppercase',
    color: mutedText(theme),
    alignSelf: 'flex-start',
    borderRadius: 2,
    '&:focus-visible': focusRing(theme, 2),
  },
  confirmMessage: {
    ...theme.typography.body2,
  },
  confirmFieldsGrid: {
    display: 'grid',
    gridTemplateColumns: 'max-content 1fr',
    columnGap: theme.spacing(1),
    rowGap: theme.spacing(0.25),
    ...theme.typography.caption,
    color: mutedText(theme),
  },
  confirmFieldValue: {
    display: 'flex',
    flexDirection: 'column',
    whiteSpace: 'pre-wrap',
    overflowWrap: 'anywhere',
  },
  confirmFieldKey: {
    fontWeight: 700,
    color: theme.palette.text.primary,
  },
  createdDisabledBadge: {
    alignSelf: 'flex-start',
    boxSizing: 'border-box',
    padding: theme.spacing(0.25, 0.75),
    borderRadius: 999,
    border: `1px solid ${theme.palette.warning.main}`,
    color: theme.palette.warning.main,
    ...theme.typography.caption,
    fontWeight: 700,
  },
  recipientCount: {
    ...theme.typography.body2,
    fontWeight: 700,
  },
  recipientSample: {
    fontWeight: 400,
    color: mutedText(theme),
  },
  confirmBlocked: {
    margin: 0,
    fontSize: 12,
    lineHeight: 1.5,
    color: mutedText(theme),
  },
  confirmActions: {
    display: 'flex',
    gap: theme.spacing(0.75),
  },
  confirmButton: {
    flex: '1 1 auto',
    boxSizing: 'border-box',
    minHeight: 36,
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    background: theme.palette.primary.main,
    color: '#fff',
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover:not(:disabled):not([aria-disabled="true"])': {
      background: alpha(theme.palette.primary.main, 0.85),
    },
    '&:focus-visible': focusRing(theme, 2),
    '&:disabled, &[aria-disabled="true"]': { cursor: 'default', opacity: 0.5 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  cancelButton: {
    flex: '1 1 auto',
    boxSizing: 'border-box',
    minHeight: 36,
    padding: theme.spacing(0.5, 1),
    borderRadius: 8,
    border: `1px solid ${controlBorder(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    fontWeight: 600,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover:not(:disabled)': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': focusRing(theme, 2),
    '&:disabled': { cursor: 'default', opacity: 0.5 },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  inlineNotice: {
    ...theme.typography.caption,
    color: mutedText(theme),
    border: `1px solid ${controlBorder(theme)}`,
    borderLeft: `3px solid ${theme.palette.text.primary}`,
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    wordBreak: 'break-word',
    flexShrink: 0,
    fontVariantNumeric: 'tabular-nums',
  },
  noticeWithAction: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
    fontSize: '13px',
    '& button': { fontSize: '13px' },
  },
  leaveFormTitle: {
    ...theme.typography.subtitle1,
    fontWeight: 700,
    lineHeight: 1.4,
    padding: theme.spacing(2, 2, 1),
  },
  leaveFormActions: {
    gap: theme.spacing(1),
    padding: theme.spacing(1, 2, 2),
    '& > :not(style) ~ :not(style)': { marginLeft: 0 },
    '& button': { fontSize: '13px' },
  },
  popOutReason: {
    fontSize: 12,
    lineHeight: 1.5,
  },
  popupStartNotice: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    flexWrap: 'wrap',
    gap: theme.spacing(1),
  },
  needsInputGroup: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
    alignItems: 'flex-start',
  },
  needsInputMessage: {
    ...theme.typography.body2,
    color: theme.palette.text.primary,
  },
  handoffButton: {
    boxSizing: 'border-box',
    display: 'inline-flex',
    alignSelf: 'flex-start',
    padding: theme.spacing(0.625, 1.25),
    borderRadius: 8,
    border: `1px solid ${theme.palette.primary.main}`,
    background: 'transparent',
    color: theme.palette.primary.main,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    fontWeight: 600,
    textDecoration: 'none',
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.primary.main, 0.08) },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  handoffPlainText: {
    ...theme.typography.caption,
    color: mutedText(theme),
  },
  composer: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    gap: theme.spacing(0.75),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${hairline(theme)}`,
    minWidth: 0,
  },
  field: {
    flex: '1 1 0',
    minWidth: 0,
    '& .MuiInputLabel-root': {
      color: alpha(theme.palette.text.primary, 0.7),
      '&.Mui-focused': { color: theme.palette.text.primary },
      '&.Mui-disabled': { color: alpha(theme.palette.text.primary, 0.55) },
    },
    '& .MuiOutlinedInput-root': {
      ...theme.typography.body2,
      color: theme.palette.text.primary,
      background: 'transparent',
      borderRadius: 8,
      '& fieldset': { borderColor: controlBorder(theme) },
      '&:hover fieldset': { borderColor: theme.palette.text.primary },
      '&.Mui-focused fieldset': { borderColor: theme.palette.text.primary, borderWidth: 2 },
      '&.Mui-disabled fieldset': { borderColor: alpha(theme.palette.text.primary, 0.28) },
      '& input::placeholder, & textarea::placeholder': {
        color: alpha(theme.palette.text.primary, 0.7),
        opacity: 1,
      },
      '&.Mui-disabled input::placeholder, &.Mui-disabled textarea::placeholder': {
        color: alpha(theme.palette.text.primary, 0.55),
        opacity: 1,
      },
    },
  },
  alphaComposerActions: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.25),
    marginLeft: theme.spacing(0.5),
    flexShrink: 0,
  },
  alphaComposerIconWrap: {
    display: 'inline-flex',
    flexShrink: 0,
  },
  alphaMicButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    padding: 0,
    color: mutedText(theme),
    transition: 'color 140ms ease-out, background-color 140ms ease-out',
    '&:hover': { color: theme.palette.text.primary, background: alpha(theme.palette.text.primary, 0.08) },
    '&:focus-visible': focusRing(theme, 2),
    '&.Mui-disabled': { color: alpha(theme.palette.text.primary, 0.4) },
  },
  alphaMicButtonRecording: {
    color: theme.palette.error.main,
    animation: '$alphaPulse 1.2s infinite ease-in-out',
    '@media (prefers-reduced-motion: reduce)': { animation: 'none' },
  },
  alphaMicButtonError: {
    color: theme.palette.error.main,
  },
  alphaVoiceButton: {
    flexShrink: 0,
    width: 32,
    height: 32,
    padding: 0,
    borderRadius: '50%',
    color: theme.palette.mode === 'dark' ? ALPHA_VOICE_COLOURS.accentLight : ALPHA_VOICE_COLOURS.primary,
    backgroundColor: alpha(ALPHA_VOICE_COLOURS.accent, 0.16),
    transition: 'background-color 140ms ease-out',
    '&:hover': { backgroundColor: alpha(ALPHA_VOICE_COLOURS.accent, 0.26) },
    '&:active': { backgroundColor: alpha(ALPHA_VOICE_COLOURS.accent, 0.34) },
    '&:focus-visible': focusRing(theme, 2),
    '&.Mui-disabled': {
      color: alpha(theme.palette.text.primary, 0.4),
      backgroundColor: alpha(theme.palette.text.primary, 0.08),
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  composerField: {
    '& .MuiInputLabel-root:not(.MuiInputLabel-shrink)': {
      maxWidth: 'calc(100% - 112px)',
    },
    '& .MuiOutlinedInput-root': {
      paddingRight: theme.spacing(0.75),
    },
  },
  voiceBadge: {
    ...theme.typography.caption,
    alignSelf: 'flex-start',
    display: 'inline-flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    color: mutedText(theme),
    fontWeight: 600,
  },
  voiceBanner: {
    flexShrink: 0,
  },
  voiceStage: {
    position: 'absolute',
    inset: 0,
    zIndex: 1,
    isolation: 'isolate',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    boxSizing: 'border-box',
    overflow: 'hidden',
    padding: theme.spacing(2, 1.5, 1.5),
    background:
      theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.surface.card,
    color: theme.palette.text.primary,
  },
  voiceStageCentre: {
    flex: '1 1 auto',
    minHeight: 0,
    width: '100%',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: theme.spacing(1),
    overflowY: 'auto',
    paddingTop: theme.spacing(4),
  },
  voiceStageCentreTop: {
    justifyContent: 'flex-start',
    paddingTop: theme.spacing(0.5),
    overflowY: 'hidden',
  },
  voiceStageHead: {
    boxSizing: 'border-box',
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(1),
    width: '100%',
    minWidth: 0,
  },
  voiceStageHeadRow: {
    flexDirection: 'row',
    alignItems: 'center',
    gap: theme.spacing(1.5),
    flexShrink: 0,
    paddingRight: theme.spacing(5),
    transition: 'gap 250ms ease-out',
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  voiceStageHeadText: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 0,
    width: '100%',
  },
  voiceStageHeadTextRow: {
    alignItems: 'flex-start',
    flex: '1 1 0',
  },
  voiceStageCaptionTop: {
    textAlign: 'left',
    minHeight: '3em',
    maxHeight: '3em',
    ...theme.typography.caption,
    lineHeight: 1.5,
  },
  voiceStageResults: {
    flex: '1 1 0',
    minHeight: 0,
    width: '100%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    paddingTop: theme.spacing(1),
    borderTop: `1px solid ${hairline(theme)}`,
    marginTop: theme.spacing(1),
    boxSizing: 'border-box',
  },
  voiceStageReport: {
    border: `1px solid ${hairline(theme)}`,
    borderRadius: 8,
    padding: theme.spacing(1),
    ...theme.typography.body2,
    overflowX: 'auto',
    overflowWrap: 'anywhere',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.5),
  },
  voiceStageNotice: {
    ...theme.typography.caption,
    color: mutedText(theme),
    border: `1px solid ${controlBorder(theme)}`,
    borderLeft: `3px solid ${theme.palette.text.primary}`,
    borderRadius: 6,
    padding: theme.spacing(0.75, 1),
    overflowWrap: 'anywhere',
    flexShrink: 0,
  },
  voiceStageOrbButton: {
    display: 'flex',
    borderRadius: '50%',
    flexShrink: 0,
  },
  voiceStageLabel: {
    ...theme.typography.caption,
    fontWeight: 700,
    letterSpacing: '0.5px',
    textTransform: 'uppercase',
    color: mutedText(theme),
    marginTop: theme.spacing(0.5),
    textAlign: 'center',
  },
  voiceStageHint: {
    ...theme.typography.caption,
    color: mutedText(theme),
    textAlign: 'center',
  },
  voiceStageCaption: {
    ...theme.typography.body2,
    lineHeight: 1.5,
    color: theme.palette.text.primary,
    textAlign: 'center',
    width: '100%',
    maxWidth: 520,
    minHeight: '4.5em',
    maxHeight: '4.5em',
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'flex-end',
    overflow: 'hidden',
    overflowWrap: 'anywhere',
  },
  voiceStageClose: {
    position: 'absolute',
    top: theme.spacing(1),
    right: theme.spacing(1),
    width: 36,
    height: 36,
    borderRadius: '50%',
    border: `1px solid ${controlBorder(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    zIndex: 3,
    transition: 'background-color 140ms ease-out',
    '&:hover': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:active': { background: alpha(theme.palette.text.primary, 0.16) },
    '&:focus-visible': focusRing(theme, 2),
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  voiceStageConfirmation: {
    width: '100%',
    flexShrink: 0,
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
  },
  voiceStageVisual: {
    width: '100%',
    minWidth: 0,
    flexShrink: 0,
  },
  voiceStageConfirmationTitle: {
    margin: theme.spacing(0, 0, 0.5),
    fontSize: 14,
    fontWeight: 700,
    lineHeight: 1.3,
    color: theme.palette.text.primary,
  },
  voiceStageSheet: {
    position: 'absolute',
    left: theme.spacing(1.5),
    right: theme.spacing(1.5),
    bottom: 104,
    maxHeight: '40%',
    overflowY: 'auto',
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(0.75),
    padding: theme.spacing(1),
    boxSizing: 'border-box',
    background:
      theme.palette.mode === 'dark' ? theme.palette.background.default : theme.palette.surface.card,
    border: `1px solid ${hairline(theme)}`,
    borderRadius: 12,
    boxShadow: `0 4px 16px ${alpha('#101828', 0.16)}`,
    zIndex: 2,
  },
  voiceStageBubbleUser: {
    ...theme.typography.body2,
    alignSelf: 'flex-end',
    background: alpha(ALPHA_VOICE_COLOURS.accent, 0.16),
    borderRadius: 10,
    padding: theme.spacing(0.75, 1.25),
    maxWidth: '85%',
    overflowWrap: 'anywhere',
  },
  voiceStageBubbleAssistant: {
    ...theme.typography.body2,
    alignSelf: 'flex-start',
    background: alpha(theme.palette.text.primary, 0.08),
    borderRadius: 10,
    padding: theme.spacing(0.75, 1.25),
    maxWidth: '85%',
    overflowWrap: 'anywhere',
  },
  voiceStageControls: {
    flexShrink: 0,
    display: 'flex',
    alignItems: 'flex-start',
    justifyContent: 'center',
    gap: theme.spacing(2),
    paddingTop: theme.spacing(1.5),
  },
  voiceStageControl: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    gap: theme.spacing(0.5),
    minWidth: 72,
  },
  voiceStageControlLabel: {
    ...theme.typography.caption,
    fontWeight: 600,
    color: theme.palette.text.primary,
  },
  voiceStageButton: {
    width: 52,
    height: 52,
    borderRadius: '50%',
    border: `1px solid ${controlBorder(theme)}`,
    background: 'transparent',
    color: theme.palette.text.primary,
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    cursor: 'pointer',
    padding: 0,
    transition: 'background-color 140ms ease-out',
    '&:hover:not(:disabled)': { background: alpha(theme.palette.text.primary, 0.08) },
    '&:active:not(:disabled)': { background: alpha(theme.palette.text.primary, 0.16) },
    '&:focus-visible': focusRing(theme, 2),
    '&:disabled': { opacity: 0.4, cursor: 'default' },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  voiceStageButtonPressed: {
    background: alpha(ALPHA_VOICE_COLOURS.accent, 0.2),
    borderColor: theme.palette.text.primary,
  },
  sendButton: {
    flex: '0 0 auto',
    boxSizing: 'border-box',
    minHeight: 40,
    padding: theme.spacing(0, 1.75),
    borderRadius: 8,
    border: `1px solid ${theme.palette.text.primary}`,
    background: alpha(theme.palette.text.primary, 0.1),
    color: theme.palette.text.primary,
    fontFamily: theme.typography.fontFamily,
    ...theme.typography.body2,
    fontWeight: 700,
    cursor: 'pointer',
    transition: 'background-color 140ms ease-out',
    '&:hover:not(:disabled)': { background: alpha(theme.palette.text.primary, 0.18) },
    '&:focus-visible': focusRing(theme, 2),
    '&:disabled': {
      cursor: 'default',
      color: alpha(theme.palette.text.primary, 0.45),
      borderColor: alpha(theme.palette.text.primary, 0.28),
      background: 'transparent',
    },
    '@media (prefers-reduced-motion: reduce)': { transition: 'none' },
  },
  srOnly: {
    position: 'absolute',
    width: 1,
    height: 1,
    padding: 0,
    margin: -1,
    overflow: 'hidden',
    clip: 'rect(0 0 0 0)',
    clipPath: 'inset(50%)',
    whiteSpace: 'nowrap',
    border: 0,
  },
}));

export default useStyles;
